import type { DeviceType } from '../pages/admin/devices/deviceTypes';

/**
 * Decide which asset types belong in the device picker for a checklist row.
 * Uses line label + category (seed templates mix several device families under “Fire Alarm System”).
 * Returns null → no filter (all building devices listed).
 */
export function inferAllowedDeviceTypesForChecklistRow(categoryLabel: string, itemLabel: string): DeviceType[] | null {
    const hay = `${itemLabel} ${categoryLabel}`.toLowerCase();

    if (/battery\s*back/i.test(hay) && (/emergency|exit|lighting/i.test(categoryLabel.toLowerCase()) || /illuminat|coverage/i.test(itemLabel.toLowerCase()))) {
        return ['emergency_light'];
    }
    if (/battery\s*back/i.test(hay)) {
        return ['control_panel'];
    }

    const rules: Array<{ test: RegExp; types: DeviceType[] }> = [
        { test: /smoke\s*detector/i, types: ['smoke_detector'] },
        { test: /heat\s*detector/i, types: ['heat_detector'] },
        { test: /pull\s*station/i, types: ['pull_station'] },
        { test: /horn|strobe/i, types: ['horn_strobe'] },
        { test: /control\s*panel/i, types: ['control_panel'] },
        { test: /exit\s*sign/i, types: ['exit_sign'] },
        { test: /illuminat/i, types: ['exit_sign'] },
        { test: /\bcoverage\b/i, types: ['emergency_light', 'exit_sign'] },
        { test: /emergency\s*light/i, types: ['emergency_light'] },
        { test: /extinguisher|pin\s+(and\s+)?tamper|last\s+inspection\s*tag/i, types: ['fire_extinguisher'] },
        { test: /sprinkler\s*head|head\s*condition/i, types: ['sprinkler_head'] },
        { test: /inspector\s*test\s*valve/i, types: ['flow_switch'] },
        { test: /main\s*control\s*valve/i, types: ['tamper_switch', 'flow_switch'] },
        { test: /\bgauge\b/i, types: ['sprinkler_head'] },
        { test: /suppression/i, types: ['suppression_system'] },
        { test: /backflow/i, types: ['backflow_preventer'] },
        { test: /\bfire\s*door\b/i, types: ['fire_door'] },
        { test: /\bdamper\b/i, types: ['damper'] }
    ];

    const matched = new Set<DeviceType>();
    for (const rule of rules) {
        if (rule.test.test(hay)) {
            rule.types.forEach((t) => matched.add(t));
        }
    }
    if (matched.size > 0) {
        return Array.from(matched);
    }

    const cat = categoryLabel.toLowerCase();
    if (/fire\s*alarm|alarm\s*system/.test(cat)) {
        return ['smoke_detector', 'heat_detector', 'pull_station', 'horn_strobe', 'control_panel'];
    }
    if (/sprinkler/.test(cat)) {
        return ['sprinkler_head', 'flow_switch', 'tamper_switch', 'backflow_preventer'];
    }
    if (/emergency\s*light|exit|\blighting\b/.test(cat)) {
        return ['emergency_light', 'exit_sign'];
    }
    if (/extinguisher|hose\s*cabinet/i.test(cat)) {
        return ['fire_extinguisher', 'other'];
    }
    if (/^23 — field devices/i.test(cat)) {
        if (/in-suite fire alarm/i.test(cat)) {
            return ['heat_detector', 'horn_strobe', 'smoke_detector', 'pull_station'];
        }
        if (/in-suite smoke alarms/i.test(cat)) {
            return ['smoke_detector'];
        }
        if (/common areas/i.test(cat)) {
            return ['smoke_detector', 'heat_detector', 'pull_station', 'horn_strobe', 'control_panel'];
        }
        if (/sprinkler devices/i.test(cat)) {
            return ['flow_switch', 'tamper_switch', 'sprinkler_head'];
        }
    }

    return null;
}
